import { createFileRoute } from '@tanstack/react-router'
import { ComparisonPage, comparisonScripts, type ComparisonData } from '@/components/comparison-page'

export const Route = createFileRoute('/_site/compare/healthchecks')({
  component: () => <ComparisonPage data={data} />,
  head: () => ({
    meta: [
      { title: 'Healthchecks.io Alternative — Uptrack Monitoring' },
      { name: 'description', content: "Uptrack vs Healthchecks.io: Healthchecks.io monitors cron jobs. Uptrack adds uptime checks, browser checks, and status pages, from $3/mo." },
      { property: 'og:title', content: 'Healthchecks.io Alternative — Uptrack' },
      { property: 'og:description', content: "Healthchecks.io focuses on cron job monitoring. Uptrack does cron, uptime, and status pages in one tool." },
      { property: 'og:image', content: 'https://uptrack.app/og/compare-healthchecks.png' },
      { name: 'twitter:card', content: 'summary_large_image' },
    ],
    links: [
      { rel: 'canonical', href: 'https://uptrack.app/compare/healthchecks' },
    ],
    scripts: comparisonScripts('healthchecks', "Healthchecks.io"),
  }),
})

const data: ComparisonData = {
  competitor: "Healthchecks.io",
  tagline: "Healthchecks.io is a focused, open-source cron job monitor with a free plan of 20 checks. If you also need uptime checks and status pages, Uptrack covers all three, from $3/mo.",
  heroDescription: "These facts come from Healthchecks.io's public pricing and docs pages as of October 2026. Plans change often, so check their site before you decide.",

  features: [
    { feature: "Cron/heartbeat monitoring", uptrack: "Included", competitor: "Core product" },
    { feature: "Uptime monitoring (HTTP)", uptrack: "Included", competitor: "Not available (passive pings only)", uptrackWins: true },
    { feature: "Status pages", uptrack: "5 on Pro, unlimited on Team", competitor: "Status badges only", uptrackWins: true },
    { feature: "SSL certificate monitoring", uptrack: "Included", competitor: "Not available", uptrackWins: true },
    { feature: "Paid plan price", uptrack: "$3/mo (50 monitors, all 30s)", competitor: "$5/mo (20 checks) or $20/mo (100 checks)", uptrackWins: true },
    { feature: "Free plan", uptrack: "5 HTTP and keyword monitors at 30 s (no cron)", competitor: "20 checks (Hobbyist)" },
    { feature: "Team members", uptrack: "3 on Pro, 5 on Team, 15 on Business", competitor: "Unlimited on all plans" },
    { feature: "Self-hosting", uptrack: "Not available", competitor: "Open source, self-hostable" },
  ],

  painPoints: [
    {
      title: "Cron monitoring only",
      description: "Healthchecks.io watches for pings from your jobs. It does not check whether your website or API responds, so uptime checks need a separate tool.",
    },
    {
      title: "No hosted status pages",
      description: "Healthchecks.io offers status badges you can embed, but no hosted status pages. Uptrack includes status pages on every plan.",
    },
    {
      title: "Several tools for full coverage",
      description: "If you need cron monitoring, uptime checks, and a status page, Healthchecks.io covers the first, and you add other tools for the rest. Uptrack Team covers all three for $20/mo, with 200 monitors.",
    },
  ],

  differentiators: [
    "All-in-one: cron monitoring, uptime monitoring, SSL monitoring, status pages, and alerting in a single tool.",
    "Cost: $20/mo for 200 monitors (cron and uptime combined) on Team, with status pages included.",
    "Alert confirmation: configurable grace periods for heartbeats, and 2-5 consecutive failed checks for uptime monitors.",
    "Status pages included: public status pages with email subscriptions. Custom domains and theming on Team and Business.",
    "Response time charts: historical response times and uptime trends for every uptime monitor.",
  ],

  notForYou: [
    "If you only need cron monitoring and nothing else, Healthchecks.io is purpose-built for that use case.",
    "If you need the open-source Healthchecks.io code for a self-hosted, on-premise deployment.",
    "If you need SMS, WhatsApp, or phone call alerts, Healthchecks.io Business includes 50 SMS/WhatsApp and 20 phone call credits.",
    "If you need unlimited team members, Healthchecks.io includes them on every plan, including free.",
    "If you need more than 5 free checks, Healthchecks.io Hobbyist includes 20 checks. Uptrack's free plan is 5 HTTP and keyword monitors at 30 seconds, and cron and heartbeat monitoring are on Pro.",
  ],
}
